
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import UnifiedSEO from '@/components/seo/UnifiedSEO';
import FAQSchema from '@/components/seo/FAQSchema';
import FAQHero from '@/components/support/FAQHero';
import FAQSearch from '@/components/support/FAQSearch';
import FAQCategoryFilter from '@/components/support/FAQCategoryFilter';
import FAQSection from '@/components/support/FAQSection';
import PopularFAQs from '@/components/support/PopularFAQs';
import ContactSection from '@/components/support/ContactSection';
import { faqs, searchFAQs, getFAQsByCategory, getFAQsByAudience } from '@/data/support/faqs';

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAudience, setSelectedAudience] = useState('all');

  const getFilteredFAQs = () => {
    let filteredFAQs = faqs;

    if (searchQuery) {
      filteredFAQs = searchFAQs(searchQuery);
    } else {
      if (selectedCategory && selectedCategory !== 'all') {
        filteredFAQs = getFAQsByCategory(selectedCategory);
      }
      if (selectedAudience && selectedAudience !== 'all') {
        filteredFAQs = filteredFAQs.filter(faq => faq.audience.includes(selectedAudience));
      }
    }

    return filteredFAQs;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedAudience('all');
  };

  const filteredFAQs = getFilteredFAQs();
  const hasActiveFilters = searchQuery || (selectedCategory !== 'all') || (selectedAudience !== 'all');

  return (
    <>
      <UnifiedSEO 
        title="Frequently Asked Questions - SeniorThrive"
        description="Find answers to common questions about SeniorThrive features, pricing, safety tools, family coordination, and getting started with aging in place."
        keywords="SeniorThrive FAQ, aging in place questions, senior care help, ThriveVision questions, family dashboard help"
        canonical="https://seniorthrive.com/support/faq"
        faq={faqs.map(faq => ({ question: faq.question, answer: faq.answer }))}
      />
      
      <FAQSchema 
        faqs={faqs.map(faq => ({ question: faq.question, answer: faq.answer, category: faq.category }))}
        pageTitle="Frequently Asked Questions - SeniorThrive"
        pageDescription="Get answers to common questions about SeniorThrive platform for safe and independent aging at home."
      />
      
      <div className="min-h-screen bg-background">
        {/* Breadcrumb */}
        <div className="bg-muted/30 py-4">
          <div className="container mx-auto px-4">
            <Link 
              to="/support" 
              className="inline-flex items-center text-primary hover:underline"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Support Center
            </Link>
          </div>
        </div>

        <FAQHero />
        
        <div className="container mx-auto px-4 py-8">
          <FAQSearch 
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
          
          <FAQCategoryFilter
            selectedCategory={selectedCategory}
            selectedAudience={selectedAudience}
            onCategoryChange={setSelectedCategory}
            onAudienceChange={setSelectedAudience}
          />

          {hasActiveFilters && (
            <div className="mb-8 flex items-center justify-between bg-muted/50 rounded-lg p-4">
              <p className="text-muted-foreground">
                Showing {filteredFAQs.length} {filteredFAQs.length === 1 ? 'result' : 'results'}
                {searchQuery && ` for "${searchQuery}"`}
              </p>
              <button 
                onClick={clearFilters}
                className="text-primary hover:underline text-sm"
              >
                Clear filters
              </button>
            </div>
          )}

          {!hasActiveFilters && <PopularFAQs />}
          
          <FAQSection faqs={filteredFAQs} />
        </div>

        <ContactSection />
      </div>
    </>
  );
};

export default FAQ;
